import { FC, useMemo } from "react";
import { Link, Stack, TableCell, TableRow } from "@mui/material";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import {
  dimensionEditActiveTab,
  dimensionIdSearchParam,
  sharedCostDistributionTypeLabels,
} from "@/components/pages/dimensions/components/form-drawer/components/dimension/utils/constants";

import { DimensionEnhancedSharedCost } from "@/store/dimensions/utils/types/common";
import { useAppSelector } from "@/store/hooks";
import { editingCategoryIdSelector } from "@/store/dimensions/selectors/slice-data/editing-category/editingCategoryIdSelector";
import { CostAllocationMetadataInCategoryEditFilters } from "@/components/pages/dimensions/components/form-drawer/components/dimension/cost-allocation/metadata-in-category-edit/CostAllocationMetadataInCategoryEditFilters";
import { dimensionCategoriesLengthSelector } from "@/store/dimensions/selectors/slice-data/categories/dimensionCategoriesLengthSelector";
import { dimensionIdSelector } from "@/store/dimensions/selectors/slice-data/dimensionIdSelector";

interface CostAllocationMetadataInCategoryEditTableRowProps {
  sharedCost: DimensionEnhancedSharedCost;
}

export const CostAllocationMetadataInCategoryEditTableRow: FC<
  CostAllocationMetadataInCategoryEditTableRowProps
> = ({ sharedCost }) => {
  const categoryId = useAppSelector(editingCategoryIdSelector);
  const dimensionId = useAppSelector(dimensionIdSelector);
  const categoriesCountInCurrentDimension = useAppSelector(
    dimensionCategoriesLengthSelector,
  );

  const shareAmount = useMemo(() => {
    if (sharedCost.distribution_type === "even") {
      return (100 / categoriesCountInCurrentDimension).toFixed(2);
    }

    return sharedCost.share_amounts?.find(
      ({ category_id }) => category_id === categoryId,
    )?.share_amount;
  }, [categoriesCountInCurrentDimension, categoryId, sharedCost]);

  return (
    <TableRow key={sharedCost.id}>
      <TableCell>
        <Link
          href={`/dimensions?${dimensionIdSearchParam}=${dimensionId}&${dimensionEditActiveTab}=sharedCost`}
          target="_blank"
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <TypographyWithTooltip
              maxWidth={200}
              variant="body2"
              title={sharedCost.name}
            />
            <OpenInNewOutlinedIcon fontSize="small" />
          </Stack>
        </Link>
      </TableCell>

      <TableCell>{shareAmount}%</TableCell>

      <TableCell>
        {sharedCostDistributionTypeLabels[sharedCost.distribution_type]}
      </TableCell>

      <TableCell>
        <CostAllocationMetadataInCategoryEditFilters
          filterTree={sharedCost.filter_tree}
        />
      </TableCell>
    </TableRow>
  );
};
